import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MessageSquarePlus,
  RefreshCw,
  Loader2,
  Filter,
  ChevronDown,
  Sparkles,
  Lightbulb,
  HelpCircle,
  Bug,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import GlassCard from "@/components/GlassCard";

/**
 * /admin/feedback — internal admin list of every user-submitted
 * feedback row. Deliberately narrow scope: list, filter, change
 * status. NO public voting / comments / replies in this phase
 * (matches the iter 78 spec).
 */

const STATUS_OPTIONS = [
  { key: "",          label: "All" },
  { key: "new",       label: "New" },
  { key: "reviewing", label: "Reviewing" },
  { key: "planned",   label: "Planned" },
  { key: "declined",  label: "Declined" },
  { key: "completed", label: "Completed" },
];

const STATUS_TONE = {
  new:       { text: "text-[color:var(--brand-primary)]",              bg: "bg-[color:rgba(27,168,159,0.10)] border-[color:rgba(27,168,159,0.35)]" },
  reviewing: { text: "text-[color:var(--warning,#eab308)]",            bg: "bg-[color:rgba(234,179,8,0.08)]  border-[color:rgba(234,179,8,0.35)]"   },
  planned:   { text: "text-[color:var(--info,#3b82f6)]",               bg: "bg-[color:rgba(59,130,246,0.08)] border-[color:rgba(59,130,246,0.35)]" },
  declined:  { text: "text-[color:var(--text-3)]",                     bg: "bg-[color:var(--surface-2)]     border-[color:var(--border-subtle)]"  },
  completed: { text: "text-[color:var(--success,#22c55e)]",            bg: "bg-[color:rgba(34,197,94,0.08)]  border-[color:rgba(34,197,94,0.35)]"  },
};

const CATEGORY_ICON = {
  feature_request:  Sparkles,
  improvement_idea: Lightbulb,
  confusing:        HelpCircle,
  bug:              Bug,
  other:            MoreHorizontal,
};

function fmtStamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (_) { return iso; }
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.new;
  const label = (STATUS_OPTIONS.find((o) => o.key === status) || {}).label || status;
  return (
    <span
      data-testid={`admin-feedback-status-${status}`}
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${tone.bg} ${tone.text}`}
    >
      {label}
    </span>
  );
}

function FeedbackRow({ row, onStatusChange, busyId }) {
  const Icon = CATEGORY_ICON[row.category] || MoreHorizontal;
  const isBusy = busyId === row.id;
  const [expanded, setExpanded] = useState(false);
  const preview = (row.message || "").slice(0, 220);
  const hasMore = (row.message || "").length > preview.length;

  return (
    <GlassCard className="p-5" data-testid={`admin-feedback-row-${row.id}`}>
      <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)] shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[color:var(--text-1)] break-words">
                {row.title || "(untitled)"}
              </div>
              <div className="mt-0.5 text-xs text-[color:var(--text-3)]">
                <span className="font-medium text-[color:var(--text-2)]">{row.category_label}</span>
                {row.importance_label && (
                  <>
                    <span className="mx-1.5">·</span>
                    <span>{row.importance_label}</span>
                  </>
                )}
                <span className="mx-1.5">·</span>
                <span>{fmtStamp(row.created_at)}</span>
              </div>
            </div>
            <StatusPill status={row.status} />
          </div>
          <div className="mt-2 text-sm text-[color:var(--text-2)] whitespace-pre-wrap break-words leading-relaxed">
            {expanded ? row.message : preview}
            {hasMore && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="ml-2 text-xs text-[color:var(--brand-primary)] hover:underline"
                data-testid={`admin-feedback-toggle-${row.id}`}
              >
                {expanded ? "show less" : "…show more"}
              </button>
            )}
          </div>
          <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-[color:var(--text-3)]">
            <span>
              <span className="text-[color:var(--text-2)]">{row.user_name || "—"}</span>
              {row.user_email && (
                <span className="ml-1">&lt;{row.user_email}&gt;</span>
              )}
            </span>
            <span>·</span>
            <span>Workspace: <span className="text-[color:var(--text-2)]">{row.workspace_name || "—"}</span></span>
            <span>·</span>
            <span>Plan: <span className="text-[color:var(--text-2)]">{row.plan || "—"}</span></span>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-3)]">
              Change status
            </span>
            <select
              value={row.status || "new"}
              disabled={isBusy}
              onChange={(e) => onStatusChange(row.id, e.target.value)}
              data-testid={`admin-feedback-status-select-${row.id}`}
              className="rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-2 py-1.5 text-xs text-[color:var(--text-1)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)] disabled:opacity-50"
            >
              {STATUS_OPTIONS.filter((o) => o.key).map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
            {isBusy && <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--text-3)]" />}
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

export default function AdminFeedback() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/admin/feedback", { params });
      setRows(Array.isArray(data?.feedback) ? data.feedback : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const onStatusChange = useCallback(async (id, next) => {
    setBusyId(id);
    try {
      const { data } = await api.patch(`/admin/feedback/${id}`, { status: next });
      setRows((prev) => prev.map((r) => (r.id === id ? (data?.feedback || r) : r)));
      toast.success(`Status → ${next}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusyId("");
    }
  }, []);

  const empty = !loading && rows.length === 0;
  const counts = useMemo(() => {
    const acc = { new: 0, reviewing: 0, planned: 0, declined: 0, completed: 0 };
    rows.forEach((r) => { if (acc[r.status] != null) acc[r.status] += 1; });
    return acc;
  }, [rows]);

  return (
    <AdminShell
      title="Feedback & Ideas"
      subtitle="Every customer submission from /feedback — filter, review, and advance status."
      icon={MessageSquarePlus}
      data-testid="admin-feedback-shell"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--text-3)]">
            <Filter className="h-3.5 w-3.5" />
            Filter
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-feedback-filter-select"
              className="appearance-none rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] pl-3 pr-8 py-1.5 text-sm text-[color:var(--text-1)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)]"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.key || "all"} value={opt.key}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="h-3.5 w-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] pointer-events-none" />
          </div>
          <div className="text-xs text-[color:var(--text-3)]">
            {rows.length} shown
            {!statusFilter && (
              <>
                <span className="mx-1.5">·</span>
                <span className="text-[color:var(--text-2)]">{counts.new}</span> new
                <span className="mx-1.5">·</span>
                <span className="text-[color:var(--text-2)]">{counts.reviewing}</span> reviewing
                <span className="mx-1.5">·</span>
                <span className="text-[color:var(--text-2)]">{counts.planned}</span> planned
              </>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="admin-feedback-refresh-btn"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading…</>
          ) : (
            <><RefreshCw className="h-4 w-4 mr-2" />Refresh</>
          )}
        </Button>
      </div>

      {loading && rows.length === 0 && (
        <GlassCard className="p-8 text-center" data-testid="admin-feedback-loading">
          <Loader2 className="h-6 w-6 mx-auto animate-spin text-[color:var(--brand-primary)]" />
          <div className="mt-3 text-sm text-[color:var(--text-2)]">Loading feedback…</div>
        </GlassCard>
      )}

      {empty && (
        <GlassCard className="p-10 text-center" data-testid="admin-feedback-empty">
          <div className="text-sm text-[color:var(--text-2)]">
            {statusFilter
              ? `No feedback in "${(STATUS_OPTIONS.find((o) => o.key === statusFilter) || {}).label}" yet.`
              : "No feedback yet."}
          </div>
        </GlassCard>
      )}

      {rows.length > 0 && (
        <div className="space-y-4">
          {rows.map((row) => (
            <FeedbackRow key={row.id} row={row} onStatusChange={onStatusChange} busyId={busyId} />
          ))}
        </div>
      )}
    </AdminShell>
  );
}
